import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { MESSAGES } from "@/modules/shared/constants/messages";
import { useToast } from "@/modules/shared/hooks/use-toast";
import type { MutationRequest } from "@/modules/shared/types/mutation-request.type";
import type { ResponseAPI } from "@/modules/shared/types/response-api.type";

import { orderTransmissionService } from "../services/order-transmission.service";
import { useRollbar } from "@rollbar/react";
import { useSyncRequest } from "@/modules/shared/hooks/use-sync-request.hook";
import { useLogger } from "@/modules/shared/hooks/use-logger.hook";
import { Variable } from "lucide-react";

export function useTransmitOrderMutation({
	callback,
	resolveRequest,
}: MutationRequest) {
	const { syncRequest } = useSyncRequest();

	const { log } = useLogger();
	const { toast } = useToast();
	return useMutation({
		mutationFn: orderTransmissionService,
		onSuccess: (data, variables, context) => {
			syncRequest(["-5"]);

			const ordersId = Array.isArray(variables.payload.orderId)
				? variables.payload.orderId.join(", ")
				: variables.payload.orderId;

			if (data?.status && data.status >= 200 && data.status < 300) {
				log(
					"success",
					"order-transmission",
					` Pedidos ${ordersId} transmitidos com sucesso`,
					{
						response: data,
						payload: variables.payload,
					},
				);
			} else {
				log(
					"error",
					"order-transmission",
					`Erro ao transmitir pedidos ${ordersId}`,
					{
						response: data,
						payload: variables.payload,
					},
				);
			}
			callback(data);
			resolveRequest({
				status: data.status,
				showToastInfo: true,
				message: data.message,
				showToastSuccess: true,
			});
		},
		onError: (error: AxiosError<ResponseAPI>, variables) => {
			log("error", "order-transmission", "Erro ao transmitir pedido", {
				error,
				payload: variables.payload,
			});
			if (
				error.response?.data?.status > 400 &&
				error.response?.data?.status < 500 &&
				error.response?.data?.message
			) {
				toast.info({
					title: error.response.data.message,
				});
			} else {
				toast.error({
					title: MESSAGES.ERROR_TITLE,
					description: MESSAGES.ERROR_DESCRIPTION,
					action: {
						label: MESSAGES.RELOAD,
						onClick: () => {
							window.location.reload();
						},
					},
				});
			}
		},
	});
}
